import { useCallback, useSyncExternalStore } from 'react';
import useSWR from 'swr';

import fetcher from 'services/fetcher';

// `GET /booking/chat` returns one row per conversation with a `lastMessageAt`
// but no read state — the API has no concept of "seen". So the last time the
// user opened each conversation is kept here, on the device, and a chat counts
// as unread while its last message is newer than that mark.
//
// Consequence worth knowing: the badge does not follow the user across devices
// or survive clearing site data. That is the ceiling of a frontend-only
// implementation; a `read_at` column on the API removes it.
export const CHAT_SEEN_STORAGE_KEY = '@contenta-chat-seen';

/** Parses the stored map, tolerating anything a hand-edited entry can contain. */
export function parseSeenMap(raw) {
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

/** True when `chat` has a message the user has not opened yet. */
export function isChatUnread(chat, seenMap = {}) {
  if (!chat?.lastMessageAt) return false;

  const seenAt = seenMap[String(chat.bookingId)];
  if (!seenAt) return true;

  const lastMessage = new Date(chat.lastMessageAt).getTime();
  const seen = new Date(seenAt).getTime();

  if (Number.isNaN(lastMessage)) return false;
  if (Number.isNaN(seen)) return true;

  return lastMessage > seen;
}

export function countUnreadChats(chats, seenMap = {}) {
  if (!Array.isArray(chats)) return 0;

  return chats.filter((chat) => isChatUnread(chat, seenMap)).length;
}

// localStorage fires no event in the tab that wrote it, so the badge and the
// conversation list are kept in step through this tiny store instead.
const listeners = new Set();
let snapshot = null;

const readStorage = () => {
  try {
    return window.localStorage.getItem(CHAT_SEEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const subscribe = (listener) => {
  listeners.add(listener);
  window.addEventListener('storage', listener);

  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
};

const getSnapshot = () => {
  const raw = readStorage();
  // useSyncExternalStore compares snapshots by identity, so the same string
  // must come back unchanged between reads or React re-renders forever.
  if (raw !== snapshot) snapshot = raw;
  return snapshot;
};

/** Records that the user just looked at this conversation. */
export function markChatAsRead(bookingId, lastMessageAt) {
  if (!bookingId) return;

  const next = {
    ...parseSeenMap(readStorage()),
    [String(bookingId)]: lastMessageAt || new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(CHAT_SEEN_STORAGE_KEY, JSON.stringify(next));
  } catch {
    return; // private mode / quota — the badge just stays on
  }

  listeners.forEach((listener) => listener());
}

/**
 * Unread conversation state for the current user.
 *
 * @param {boolean} enabled pass false to skip the request entirely (logged out,
 *   or a role with no chat at all, such as ADMIN)
 */
export default function useUnreadChats(enabled = true) {
  const rawSeen = useSyncExternalStore(subscribe, getSnapshot, () => null);
  const seenMap = parseSeenMap(rawSeen);

  // Same SWR key the chat page uses, so opening the page reuses this cache
  // instead of firing a second request.
  const { data } = useSWR(enabled ? '/booking/chat' : null, fetcher, {
    refreshInterval: 60000,
    revalidateOnReconnect: true,
  });

  const markAsRead = useCallback(
    (bookingId) => {
      const chat = data?.find?.(
        (item) => String(item.bookingId) === String(bookingId)
      );
      markChatAsRead(bookingId, chat?.lastMessageAt);
    },
    [data]
  );

  return {
    chats: data,
    seenMap,
    unreadCount: countUnreadChats(data, seenMap),
    isUnread: (chat) => isChatUnread(chat, seenMap),
    markAsRead,
  };
}
